import { toast } from 'react-toastify';
import * as XLSX from 'xlsx-js-style';
import appApi from '../../../../api/app-api';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import dateUtils from '../../../../utils/date-format';
import {
  AttendanceStatusDb,
  CourseLectureAttendancePart,
  CourseLectureAttendanceReport,
} from '../../../../api/types/programs/course/attendance/course-lecture-attendance-report.type';
import { LectureStatus } from '../../../../api/types/programs/course/lectures/lecture-status.type';
import globalCourseStore from '../../../../stores/global-course-store';

type ExcelCell = string | number | boolean | Date | null | undefined;

type PartColumn = {
  part: CourseLectureAttendancePart;
  lecture: CourseLectureAttendanceReport;
};

type LectureColumnSpan = {
  lecture: CourseLectureAttendanceReport;
  startCol: number;
  partCount: number;
};

const SUMMARY_SEPARATOR_ROWS = 1;

type AttendanceExcelLayout = {
  summaryStartRow: number;
  summaryRowCount: number;
  headerRow0Index: number;
  headerRow1Index: number;
  dataStartRow: number;
};

type AttendanceAggregation = {
  totalLectures: number;
  initLectures: number;
  startLectures: number;
  finishedLectures: number;
  totalAttendanceParts: number;
  attendedCount: number;
  lateCount: number;
  missedCount: number;
};

const computeAttendanceAggregation = (
  lectures: CourseLectureAttendanceReport[],
): AttendanceAggregation => {
  let initLectures = 0;
  let startLectures = 0;
  let finishedLectures = 0;
  let totalAttendanceParts = 0;
  let attendedCount = 0;
  let lateCount = 0;
  let missedCount = 0;

  lectures.forEach((lecture) => {
    switch (lecture.lectureStatus) {
      case LectureStatus.INIT:
        initLectures++;
        break;
      case LectureStatus.START:
        startLectures++;
        break;
      case LectureStatus.FINISHED:
        finishedLectures++;
        break;
    }

    const parts = getLectureParts(lecture);
    totalAttendanceParts += parts.length;

    if (lecture.lectureStatus === LectureStatus.INIT) {
      return;
    }

    parts.forEach((part) => {
      part.attendance.forEach((entry) => {
        switch (entry.status) {
          case AttendanceStatusDb.Attended:
            attendedCount++;
            break;
          case AttendanceStatusDb.Late:
            lateCount++;
            break;
          case AttendanceStatusDb.Missed:
            missedCount++;
            break;
        }
      });
    });
  });

  return {
    totalLectures: lectures.length,
    initLectures,
    startLectures,
    finishedLectures,
    totalAttendanceParts,
    attendedCount,
    lateCount,
    missedCount,
  };
};

const buildSummaryRows = (
  aggregation: AttendanceAggregation,
): ExcelCell[][] => [
  ['מספר הרצאות סה״כ', aggregation.totalLectures],
  ['מספר הרצאות שטרם התחילו', aggregation.initLectures],
  ['מספר הרצאות שהתחילו', aggregation.startLectures],
  ['מספר הרצאות שהסתיימו', aggregation.finishedLectures],
  ['מספר מקטעי נוכחות', aggregation.totalAttendanceParts],
  ['מספר נוכחים', aggregation.attendedCount],
  ['מספר מאחרים', aggregation.lateCount],
  ['מספר מחסירים', aggregation.missedCount],
];

const attendanceStatusLabel = (
  status: AttendanceStatusDb | null | undefined,
): string => {
  if (status === null || status === undefined) {
    return 'טרם הוזן';
  }

  switch (status) {
    case AttendanceStatusDb.Attended:
      return 'נוכח';
    case AttendanceStatusDb.Late:
      return 'איחור';
    case AttendanceStatusDb.Missed:
      return 'חיסור';
    default:
      return String(status);
  }
};

const getAttendanceCellValue = (
  part: CourseLectureAttendancePart,
  lecture: CourseLectureAttendanceReport,
  studentId: number,
): string => {
  const entry = part.attendance.find((a) => a.studentId === studentId);
  if (!entry) {
    return 'טרם הוזן';
  }

  return attendanceStatusLabel(entry.status);
};

const formatLectureColumnHeader = (
  lecture: CourseLectureAttendanceReport,
): string => {
  return dateUtils.displayFormatDate(new Date(lecture.date));
};

const getLectureParts = (
  lecture: CourseLectureAttendanceReport,
): CourseLectureAttendancePart[] => {
  return lecture.parts.length > 0 ? lecture.parts : [];
};

const fetchAllCourseStudentAttendance = async (
  programId: number,
  courseId: number,
  startDate: Date | null,
  endDate: Date | null,
): Promise<CourseLectureAttendanceReport[]> => {
  const limit = 500;
  let offset = 0;
  let total = 0;
  const allRows: CourseLectureAttendanceReport[] = [];

  do {
    const res = await appApi.getCourseStudentAttendance(programId, courseId, {
      startDate,
      endDate,
      offset,
      limit,
    });

    if (res.data.errCode) {
      throw new Error(String(res.data.errCode));
    }

    const page = res.data.body;
    total = page?.total ?? 0;
    allRows.push(...(page?.data ?? []));
    offset += limit;
  } while (allRows.length < total);

  return allRows.sort(
    (a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime() ||
      a.startTime.localeCompare(b.startTime),
  );
};

const buildAttendanceExcelData = (
  lectures: CourseLectureAttendanceReport[],
  students: [number, string][],
): {
  data: ExcelCell[][];
  merges: XLSX.Range[];
  layout: AttendanceExcelLayout;
} => {
  const aggregation = computeAttendanceAggregation(lectures);
  const summaryRows = buildSummaryRows(aggregation);
  const summaryRowCount = summaryRows.length;
  const attendanceTableStartRow = summaryRowCount + SUMMARY_SEPARATOR_ROWS;
  const headerRow0Index = attendanceTableStartRow;
  const headerRow1Index = attendanceTableStartRow + 1;
  const dataStartRow = attendanceTableStartRow + 2;

  const layout: AttendanceExcelLayout = {
    summaryStartRow: 0,
    summaryRowCount,
    headerRow0Index,
    headerRow1Index,
    dataStartRow,
  };

  const headerRow0: ExcelCell[] = ['שם הסטודנט'];
  const headerRow1: ExcelCell[] = [''];
  const merges: XLSX.Range[] = [
    { s: { r: headerRow0Index, c: 0 }, e: { r: headerRow1Index, c: 0 } },
  ];

  const partColumns: PartColumn[] = [];
  const lectureSpans: LectureColumnSpan[] = [];
  let colIndex = 1;

  lectures.forEach((lecture) => {
    const parts = getLectureParts(lecture);
    const startCol = colIndex;

    headerRow0.push(formatLectureColumnHeader(lecture));
    for (let i = 1; i < parts.length; i++) {
      headerRow0.push('');
    }

    parts.forEach((part, idx) => {
      headerRow1.push(part.name?.trim() || `חלק ${idx + 1}`);
      partColumns.push({ part, lecture });
      colIndex++;
    });

    if (parts.length > 0) {
      lectureSpans.push({ lecture, startCol, partCount: parts.length });
    }

    if (parts.length > 1) {
      merges.push({
        s: { r: headerRow0Index, c: startCol },
        e: { r: headerRow0Index, c: startCol + parts.length - 1 },
      });
    }
  });

  const initLectureStartColById = new Map(
    lectureSpans
      .filter(({ lecture }) => lecture.lectureStatus === LectureStatus.INIT)
      .map(({ lecture, startCol }) => [lecture.id, startCol] as const),
  );

  const dataRows = students.map(([studentId, studentName], studentIdx) => [
    studentName,
    ...partColumns.map(({ part, lecture }, partIdx) => {
      const initStartCol = initLectureStartColById.get(lecture.id);
      if (initStartCol !== undefined) {
        return studentIdx === 0 && partIdx + 1 === initStartCol
          ? 'טרם בוצע'
          : '';
      }

      return getAttendanceCellValue(part, lecture, studentId);
    }),
  ]);

  if (students.length > 0) {
    lectureSpans.forEach(({ lecture, startCol, partCount }) => {
      if (lecture.lectureStatus === LectureStatus.INIT && partCount > 0) {
        merges.push({
          s: { r: dataStartRow, c: startCol },
          e: {
            r: dataStartRow + students.length - 1,
            c: startCol + partCount - 1,
          },
        });
      }
    });
  }

  return {
    data: [...summaryRows, [], headerRow0, headerRow1, ...dataRows],
    merges,
    layout,
  };
};

const applyAttendanceHeaderStyle = (
  cell: XLSX.CellObject,
  border: {
    top: { style: string; color: { rgb: string } };
    bottom: { style: string; color: { rgb: string } };
    left: { style: string; color: { rgb: string } };
    right: { style: string; color: { rgb: string } };
  },
): void => {
  cell.s = {
    alignment: {
      horizontal: 'right',
      vertical: 'center',
      wrapText: true,
    },
    border,
    fill: {
      patternType: 'solid',
      fgColor: { rgb: '1F4E79' },
    },
    font: { bold: true, color: { rgb: 'FFFFFF' } },
  };
};

const applyAttendanceHeaderMerges = (
  worksheet: XLSX.WorkSheet,
  merges: XLSX.Range[],
  layout: AttendanceExcelLayout,
): void => {
  (worksheet as XLSX.WorkSheet & { '!merges'?: XLSX.Range[] })['!merges'] =
    merges;

  const ref = worksheet['!ref'];
  if (!ref) {
    return;
  }

  const range = XLSX.utils.decode_range(ref);
  const border = {
    top: { style: 'thin', color: { rgb: 'D0D5DD' } },
    bottom: { style: 'thin', color: { rgb: 'D0D5DD' } },
    left: { style: 'thin', color: { rgb: 'D0D5DD' } },
    right: { style: 'thin', color: { rgb: 'D0D5DD' } },
  } as const;

  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerAddr = XLSX.utils.encode_cell({ r: layout.headerRow0Index, c });
    const headerCell = worksheet[headerAddr];
    if (headerCell) {
      applyAttendanceHeaderStyle(headerCell, border);
    }

    const partAddr = XLSX.utils.encode_cell({ r: layout.headerRow1Index, c });
    const partCell = worksheet[partAddr];
    if (!partCell) {
      continue;
    }

    applyAttendanceHeaderStyle(partCell, border);
  }

  merges.forEach((merge) => {
    if (merge.s.r < layout.dataStartRow) {
      return;
    }

    const addr = XLSX.utils.encode_cell(merge.s);
    const cell = worksheet[addr];
    if (!cell) {
      return;
    }

    cell.s = {
      alignment: {
        horizontal: 'center',
        vertical: 'center',
        wrapText: true,
      },
      border,
      fill: {
        patternType: 'solid',
        fgColor: { rgb: 'D9E2F3' },
      },
      font: { color: { rgb: '111827' } },
    };
  });

  for (
    let r = layout.summaryStartRow;
    r < layout.summaryStartRow + layout.summaryRowCount;
    r++
  ) {
    for (let c = 0; c <= 1; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[addr];
      if (!cell) {
        continue;
      }

      cell.s = {
        alignment: {
          horizontal: c === 0 ? 'right' : 'center',
          vertical: 'center',
          wrapText: true,
        },
        border,
        fill: {
          patternType: 'solid',
          fgColor: { rgb: 'F3F4F6' },
        },
        font: {
          bold: c === 0,
          color: { rgb: '111827' },
        },
      };
    }
  }

  const rows = ((
    worksheet as XLSX.WorkSheet & { '!rows'?: { hpt?: number }[] }
  )['!rows'] ?? [{ hpt: 20 }]) as { hpt?: number }[];
  rows[layout.headerRow1Index] = { hpt: 20 };
  (worksheet as XLSX.WorkSheet & { '!rows'?: { hpt?: number }[] })['!rows'] =
    rows;
};

export const exportStudentAttendanceToExcelOperation = async (
  programId: number | undefined,
  courseId: number | undefined,
  startDate: Date | null,
  endDate: Date | null,
  setLoading: (loading: boolean) => void,
  onSuccess?: () => void,
) => {
  try {
    if (!programId || !courseId) {
      toast.warn('לא ניתן לאתר את פרטי התוכנית ו/או הקורס');
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      toast.warn('תאריך ההתחלה חייב להיות לפני תאריך הסיום');
      return;
    }

    setLoading(true);

    const lectures = await fetchAllCourseStudentAttendance(
      programId,
      courseId,
      startDate,
      endDate,
    );

    if (lectures.length === 0) {
      toast.info('לא נמצאו נתוני נוכחות בטווח התאריכים שנבחר');
      return;
    }

    const studentsById = new Map<number, string>();
    lectures.forEach((lecture) => {
      getLectureParts(lecture).forEach((part) => {
        part.attendance.forEach((entry) => {
          if (!studentsById.has(entry.studentId)) {
            studentsById.set(entry.studentId, entry.studentName);
          }
        });
      });
    });

    const students = Array.from(studentsById.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], 'he'),
    );

    if (students.length === 0) {
      toast.info('לא נמצאו סטודנטים בדוח הנוכחות');
      return;
    }

    const { data, merges, layout } = buildAttendanceExcelData(
      lectures,
      students,
    );

    const courseName =
      globalCourseStore.getCurrentCourse()?.metadata?.abstractData
        ?.courseName ?? 'קורס';

    generateAndDownloadExcelFile(data, {
      fileName: `נוכחות סטודנטים - ${courseName}`,
      sheetName: 'נוכחות',
      postProcessWorksheet: (worksheet) =>
        applyAttendanceHeaderMerges(worksheet, merges, layout),
    });

    toast.success('הקובץ יוצא בהצלחה');
    onSuccess?.();
  } catch (error) {
    console.error('exportStudentAttendanceToExcelOperation failed', error);
    toast.error('משהו קרה, לא ניתן לייצא את דוח הנוכחות');
  } finally {
    setLoading(false);
  }
};
